import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export type Lang = 'en' | 'vn';

interface LabelItem {
    key: string;
    english: string | null;
    vietnamese: string | null;
}

interface LabelMap {
    [key: string]: { en: string; vn: string };
}

const CACHE_PREFIX = 'labels_';
const LANG_KEY = 'appLang';
// Custom event to sync lang state across all useLang instances in the same tab
const LANG_CHANGE_EVENT = 'appLangChange';

export function useLang(): [Lang, (l: Lang) => void] {
    const [lang, setLangState] = useState<Lang>(
        () => (localStorage.getItem(LANG_KEY) as Lang) ?? 'en'
    );

    // Sync data-lang attribute on <html> on mount
    useEffect(() => {
        const stored = (localStorage.getItem(LANG_KEY) as Lang) ?? 'en';
        document.documentElement.setAttribute('data-lang', stored);
    }, []);

    // Listen for lang changes dispatched by other useLang instances
    useEffect(() => {
        const handler = (e: Event) => {
            setLangState((e as CustomEvent<Lang>).detail);
        };
        window.addEventListener(LANG_CHANGE_EVENT, handler);
        return () => window.removeEventListener(LANG_CHANGE_EVENT, handler);
    }, []);

    const setLang = useCallback((l: Lang) => {
        localStorage.setItem(LANG_KEY, l);
        document.documentElement.setAttribute('data-lang', l);
        setLangState(l);
        // Broadcast to ALL other useLang instances in this tab
        window.dispatchEvent(new CustomEvent<Lang>(LANG_CHANGE_EVENT, { detail: l }));
        // Clear label cache so useLabels re-fetches fresh data
        Object.keys(sessionStorage)
            .filter((k) => k.startsWith(CACHE_PREFIX))
            .forEach((k) => sessionStorage.removeItem(k));
    }, []);

    return [lang, setLang];
}

export function useLabels(category: string, token: string, lang: Lang = 'en') {
    const [labelMap, setLabelMap] = useState<LabelMap>({});
    const [ready, setReady] = useState(false);
    // Increments when lang changes → forces re-fetch after cache is cleared
    const [fetchTick, setFetchTick] = useState(0);

    useEffect(() => {
        const handler = () => setFetchTick((n) => n + 1);
        window.addEventListener(LANG_CHANGE_EVENT, handler);
        return () => window.removeEventListener(LANG_CHANGE_EVENT, handler);
    }, []);

    useEffect(() => {
        if (!token) return;

        const cacheKey = CACHE_PREFIX + category;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            setLabelMap(JSON.parse(cached));
            setReady(true);
            return;
        }

        axios
            .get(`${process.env.REACT_APP_API_END_POINT}/v1/LanguageLabel/Labels`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { category, limit: 200, offset: 0 },
            })
            .then((res) => {
                const list: LabelItem[] = res.data?.items ?? res.data?.labels ?? res.data ?? [];
                const map: LabelMap = {};
                list.forEach((l) => {
                    map[l.key] = {
                        en: l.english ?? '',
                        vn: l.vietnamese ?? '',
                    };
                });
                sessionStorage.setItem(cacheKey, JSON.stringify(map));
                setLabelMap(map);
            })
            .catch((err) => console.error(`[LANG] error ${category}:`, err?.response?.status, err?.message))
            .finally(() => setReady(true));
    }, [token, category, fetchTick]);

    const t = useCallback(
        (key: string, fallback: string = ''): string => {
            const entry = labelMap[key];
            if (!entry) return fallback;
            const val = lang === 'vn' ? entry.vn : entry.en;
            return val || fallback;
        },
        [labelMap, lang]
    );

    return { t, ready };
}
